/**
 * CAHS glyph render-mode flag (#308 founder amendment — the monochrome /
 * gov-compliance mode; design ruling docs/cahs-design-council-2026-06-15.md
 * PART 0 + FOUNDER AMENDMENT 4).
 *
 * CAHS is monochrome BY DESIGN — a black foreground glyph on white, with colour
 * explicitly delegated to the operator (PART 0). Our default rendering fills
 * that slot with our token colour law (URGENT-RED → ACK-STEEL → ambient →
 * ghost). This flag offers the OTHER selectable reading: a CAHS-native /
 * gov-compliance MONOCHROME mode where every glyph renders in one neutral ink
 * with NO our-colour overlay. In mono mode urgency is still fully legible — it
 * is carried by SIZE (Response Level), the L4 RING, MOTION (the pulse), and the
 * text label — exactly the channels the marker doctrine already uses; only the
 * glyph HUE is dropped. Because CAHS is monochrome-native, this is a natural
 * toggle, not a colour fight (PART 0).
 *
 * Default rendering stays our token colour law ({@link DEFAULT_CAHS_MONO_ON} =
 * false); mono is OPT-IN. The flip is this one constant — no other code change.
 *
 * Dependency-free and resolved live (mirrors {@link ./demoMode} /
 * {@link ./renderResolution}) so the iconRegistry CAHS retint path can read it
 * at glyph-build time without pulling in React/prefs state. Read at entity-build
 * time, not per frame.
 *
 * NB: this is the SEAM only. No live marker family consumes CAHS glyphs yet
 * (that is #305) — so flipping mono changes nothing operator-visible today; it
 * exists so #305 keying inherits a working, tested gov-mode toggle for free.
 */

/**
 * The single neutral ink used in monochrome / gov-compliance mode. A light
 * near-white that reads as a CAHS-native monochrome glyph on the dark COP, and
 * is deliberately NONE of the reserved tokens (not URGENT-RED, not ACK-STEEL,
 * not LIVE-GREEN) — in mono mode the glyph carries no register hue at all.
 */
export const CAHS_MONO_INK = '#e7ecf3';

/** localStorage key for the sticky per-browser mono-mode toggle. */
export const CAHS_MONO_STORAGE_KEY = 'tsr.cahsMono';

/**
 * Default when nothing else decides: FALSE = our token colour law renders the
 * glyph ink (URGENT-RED / ACK-STEEL / ambient / ghost). Flip to `true` for a
 * global gov-compliance monochrome COP — the one-line switch, no other change.
 */
export const DEFAULT_CAHS_MONO_ON = false;

/** Test seam: pin mono on/off, or `null` to fall back to live resolution. */
let override: boolean | null = null;

/** Test seam — pin mono-mode for a test, or `null` to restore live resolution. */
export function __setCahsMonochromeOverride(value: boolean | null): void {
  override = value;
}

function truthyParam(value: string | null): boolean | null {
  if (value == null || value === '') return null;
  if (value === '0' || value === 'false' || value === 'off') return false;
  return true;
}

/**
 * Is CAHS monochrome / gov-compliance mode active for this load? Resolved live,
 * in precedence order (most explicit first):
 *   1. test override.
 *   2. `?cahsMono=` URL param (`cahsMono=1|on|true` ON; `=0|off|false` OFF) — a
 *      shareable gov-demo URL / the per-load escape hatch.
 *   3. `localStorage['tsr.cahsMono']` — a sticky per-browser choice: `'1'` opts IN,
 *      `'0'` opts OUT. The explicit `'0'` opt-out matters once
 *      {@link DEFAULT_CAHS_MONO_ON} is flipped global-ON — it keeps a single
 *      machine on our colour law (the `renderResolution` opt-out idiom).
 *   4. {@link DEFAULT_CAHS_MONO_ON}.
 * Returns the default outside a browser context.
 */
export function isCahsMonochromeMode(): boolean {
  if (override !== null) return override;
  try {
    const fromQuery = truthyParam(new URLSearchParams(window.location.search).get('cahsMono'));
    if (fromQuery !== null) return fromQuery;
    const stored = window.localStorage.getItem(CAHS_MONO_STORAGE_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
    return DEFAULT_CAHS_MONO_ON;
  } catch {
    return DEFAULT_CAHS_MONO_ON;
  }
}

/**
 * The monochrome mode the CURRENT page load built its glyphs against — captured
 * ONCE at module init (≈ the first glyph build, since iconRegistry imports this
 * module), NOT per React mount. This is the STABLE baseline the in-UI toggle's
 * "Reload to apply" affordance compares the pending choice against, so the
 * affordance survives the Settings menu unmounting/remounting and only clears on
 * an actual page reload (when this module re-initialises). Comparing against a
 * per-mount snapshot instead would lose the cue the moment the menu is reopened.
 */
let renderedCahsMonochromeAtLoad = isCahsMonochromeMode();

/** The monochrome mode the current load rendered its glyphs against — the
 *  load-time baseline (see {@link renderedCahsMonochromeAtLoad}). */
export function renderedCahsMonochromeMode(): boolean {
  return renderedCahsMonochromeAtLoad;
}

/** Test seam — re-capture the load-time baseline from the current resolution,
 *  simulating a fresh page load with the present override / URL / localStorage. */
export function __resetRenderedCahsMonochromeBaseline(): void {
  renderedCahsMonochromeAtLoad = isCahsMonochromeMode();
}

/**
 * Persist the sticky per-browser monochrome / gov-compliance choice — the writer
 * the in-UI toggle (#308) drives, co-located with the resolver that reads it.
 * Always writes an EXPLICIT `'1'` (opt IN) / `'0'` (opt OUT) rather than removing
 * the key: the definite `'0'` is what keeps a machine on our colour law once
 * {@link DEFAULT_CAHS_MONO_ON} is ever flipped global-ON (the {@link isCahsMonochromeMode}
 * precedence relies on it). Swallows storage errors (private mode / quota) — the
 * choice just isn't sticky that session. NB: the retint is resolved at glyph-BUILD
 * time, so a persisted flip takes visual effect on the next COP load, not live.
 *
 * Also strips any `?cahsMono=` URL param: that param OUTRANKS localStorage in
 * {@link isCahsMonochromeMode}, so once the user makes a sticky choice we clear
 * the per-load param (the shareable gov-demo escape hatch) and let localStorage
 * become authoritative — otherwise the user's toggle would be silently overridden
 * on the next load and the "Reload to apply" affordance would lie. Best-effort.
 */
export function setCahsMonochromeMode(on: boolean): void {
  try {
    window.localStorage.setItem(CAHS_MONO_STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* private mode / quota — the choice just isn't sticky this session */
  }
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('cahsMono')) {
      url.searchParams.delete('cahsMono');
      window.history.replaceState(window.history.state, '', url);
    }
  } catch {
    /* no window / malformed URL — stripping the per-load param is best-effort */
  }
}

/**
 * Resolve the ink a CAHS glyph should render with. In our-colour-law mode
 * (default) the family token colour passes through unchanged, so lifecycle
 * recolour + ambient/act registers all work. In monochrome mode the neutral
 * {@link CAHS_MONO_INK} replaces it — urgency is then read via size / ring /
 * motion / label, never hue.
 *
 * `mono` defaults to the live flag ({@link isCahsMonochromeMode}); pass an
 * explicit boolean to force a register at the call site (e.g. a legend swatch).
 */
export function resolveCahsInk(tokenColor: string, mono?: boolean): string {
  const monochrome = mono ?? isCahsMonochromeMode();
  return monochrome ? CAHS_MONO_INK : tokenColor;
}
